import "./CustomColor.less";
import "./App.less";

import { App as AntdApp, ConfigProvider, Grid, Spin, theme } from "antd";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import Default from "src/features/layout/Default";
import Login from "src/features/auth/Login";
import { AuthProvider } from "src/features/auth/AuthContext";
import PrivateRoute from "src/routes/PrivateRoute";

import { useScreenStore } from "src/stores/screenStore";
import { useThemeStore } from "src/stores/ThemeStore";

import DashboardPage from "src/features/dashboard/DashboardPage";
const FinanceHistory = lazy(() => import("src/features/finance/FinanceHistory"));
const FinanceAnalysis = lazy(() => import("src/features/finance/FinanceAnalysis"));
const FinanceAssets = lazy(() => import("src/features/finance/FinanceAssets"));
const RentalManage = lazy(() => import("src/features/finance/RentalManage"));
const RetirementSimulator = lazy(() => import("src/features/finance/RetirementSimulator"));
const PensionTaxSimulator = lazy(() => import("src/features/finance/PensionTaxSimulator"));
const PortfolioPage = lazy(() => import("src/features/investment/PortfolioPage"));
const StocksPage = lazy(() => import("src/features/investment/StocksPage"));
const FundsPage = lazy(() => import("src/features/investment/FundsPage"));
const MoneyMarketsPage = lazy(() => import("src/features/investment/MoneyMarketsPage"));
const MarketInsightsPage = lazy(() => import("src/features/investment/MarketInsightsPage"));
const MarketDeskPage = lazy(() => import("src/features/investment/MarketDeskPage"));
const ScreenerPage = lazy(() => import("src/features/investment/ScreenerPage"));
const TradeTimingsPage = lazy(() => import("src/features/investment/TradeTimingsPage"));
const ContestPage = lazy(() => import("src/features/investment/ContestPage"));
const SettingsCategoriesPage = lazy(() => import("src/features/settings/SettingsCategoriesPage"));
const SettingsNotificationPatternsPage = lazy(() => import("src/features/settings/SettingsNotificationPatternsPage"));
const SettingsNotificationsPage = lazy(() => import("src/features/settings/SettingsNotificationsPage"));
const SettingsHolidaysPage = lazy(() => import("src/features/settings/SettingsHolidaysPage"));
import PlaceholderPage from "src/components/common/PlaceholderPage";
import NhTradingPage from "src/features/trading/NhTradingPage";
const AIInsightsPage = lazy(() => import("src/features/investment/AIInsightsPage"));

const { useBreakpoint } = Grid;

const App = () => {
  const { defaultAlgorithm, darkAlgorithm } = theme;
  const screens = useBreakpoint();
  const setScreens = useScreenStore((state) => state.setScreens);
  const isDarkMode = useThemeStore((state) => state.isDarkMode);

  useEffect(() => {
    setScreens(screens);
  }, [screens, setScreens]);

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
        cssVar: { prefix: "app" },
        hashed: false,
        token: {
          borderRadius: 6,
          fontFamily:
            '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", sans-serif',
        },
      }}
    >
      <AntdApp>
        <AuthProvider>
          <Router>
            <Suspense fallback={<div style={{ display: "flex", justifyContent: "center", padding: 120 }}><Spin size="large" /></div>}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<PrivateRoute element={<Default />} />}>
                <Route index element={<DashboardPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />

                {/* 자산관리 */}
                <Route path="/finance/inout" element={<FinanceHistory />} />
                <Route path="/finance/analysis" element={<FinanceAnalysis />} />
                <Route path="/finance/assets" element={<FinanceAssets />} />
                <Route path="/finance/rental" element={<RentalManage />} />
                <Route path="/finance/retirement-sim" element={<RetirementSimulator />} />
                <Route path="/finance/pension-tax-sim" element={<PensionTaxSimulator />} />

                {/* 투자 */}
                <Route path="/investment/portfolio" element={<PortfolioPage />} />
                <Route path="/investment/stocks" element={<StocksPage />} />
                <Route path="/investment/funds" element={<FundsPage />} />
                <Route path="/investment/money-markets" element={<MoneyMarketsPage />} />
                <Route path="/investment/insights" element={<AIInsightsPage />} />
                <Route path="/research/market-insights" element={<MarketInsightsPage />} />
                <Route path="/research/market-desk" element={<MarketDeskPage />} />
                <Route path="/research/screener" element={<ScreenerPage />} />
                <Route path="/research/trade-timings" element={<TradeTimingsPage />} />
                <Route path="/research/contest" element={<ContestPage />} />

                {/* 트레이딩 */}
                <Route path="/trading/quant" element={<PlaceholderPage title="퀀트" />} />
                <Route path="/trading/kis" element={<PlaceholderPage title="한투 투자" />} />
                <Route path="/trading/nh" element={<NhTradingPage />} />

                {/* AI */}
                <Route path="/assistant" element={<PlaceholderPage title="AI 어시스턴트" />} />

                {/* 설정 */}
                <Route path="/settings/categories" element={<SettingsCategoriesPage />} />
                <Route path="/settings/notification-patterns" element={<SettingsNotificationPatternsPage />} />
                <Route path="/settings/notifications" element={<SettingsNotificationsPage />} />
                <Route path="/settings/holidays" element={<SettingsHolidaysPage />} />
                <Route path="/profile" element={<PlaceholderPage title="프로필" />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </Router>
        </AuthProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
